import { ApiEnvelope } from './migrations';
import { ApiError, getJson } from './client';

// --- GET /v1/tenants/resolve?domain=<domain> (FD-110 Phase A, v1.0) ----------
// Migration-agnostic admin read (EasyAuth per ADR-014). Resolves a domain to
// its Entra tenant GUID via the unauthenticated OIDC discovery document,
// server-side (manager/resolve-tenant). The UI resolves + previews before
// submit — this is the PRIMARY domain-resolution path per the FD-110 contract.
//
// Contract row (docs/TASKS.md API Contract Changes, FD-110 v1.0):
//   200 data.{domain, tenantId, issuer}
//   400 INVALID_DOMAIN                 — missing/malformed domain
//   404 TENANT_NOT_FOUND_FOR_DOMAIN    — discovery 400/404, definitive, no retry
//   502 DISCOVERY_ERROR                — network/5xx/timeout, or unparseable issuer
//
// Keep this interface aligned with that row (frontend/CLAUDE.md data-sync rule).

/** Unwrapped `data` payload of GET /v1/tenants/resolve (FD-110 v1.0). */
export interface TenantResolveResponse {
  /** The resolved domain, normalized (trimmed + lowercased) by the server. */
  domain: string;
  /** The Entra tenant GUID parsed from the discovery `issuer`. */
  tenantId: string;
  /**
   * The OIDC issuer the GUID was parsed from — `login.microsoftonline.com/{guid}/v2.0`
   * or the legacy `sts.windows.net/{guid}/` form. Surfaced for completeness; the
   * UI displays `domain` + `tenantId`.
   */
  issuer: string;
}

/** The machine `error.code`s the resolve endpoint returns (FD-110 v1.0). */
export type TenantResolveErrorCode =
  | 'INVALID_DOMAIN'
  | 'TENANT_NOT_FOUND_FOR_DOMAIN'
  | 'DISCOVERY_ERROR';

export async function resolveTenant(domain: string): Promise<TenantResolveResponse> {
  const envelope = await getJson<ApiEnvelope<TenantResolveResponse>>(
    `/v1/tenants/resolve?domain=${encodeURIComponent(domain)}`,
  );
  return envelope.data;
}

// Surfaces the resolve endpoint's distinct machine codes from a thrown ApiError.
// Prefers the envelope's `error.code` (authoritative) and falls back to the HTTP
// status → code mapping the FD-110 contract pins, so callers can branch on the
// code even when the body is unavailable (mirrors the status-based branching the
// rest of the API layer uses).
export function tenantResolveErrorCode(err: unknown): TenantResolveErrorCode | 'UNKNOWN' {
  if (!(err instanceof ApiError)) return 'UNKNOWN';

  const envelopeCode = readEnvelopeErrorCode(err.body);
  if (
    envelopeCode === 'INVALID_DOMAIN' ||
    envelopeCode === 'TENANT_NOT_FOUND_FOR_DOMAIN' ||
    envelopeCode === 'DISCOVERY_ERROR'
  ) {
    return envelopeCode;
  }

  switch (err.status) {
    case 400:
      return 'INVALID_DOMAIN';
    case 404:
      return 'TENANT_NOT_FOUND_FOR_DOMAIN';
    case 502:
      return 'DISCOVERY_ERROR';
    default:
      return 'UNKNOWN';
  }
}

function readEnvelopeErrorCode(body: unknown): string | undefined {
  if (body && typeof body === 'object' && 'error' in body) {
    const error = (body as { error: unknown }).error;
    if (error && typeof error === 'object' && 'code' in error) {
      const code = (error as { code: unknown }).code;
      return typeof code === 'string' ? code : undefined;
    }
  }
  return undefined;
}

// --- GET /v1/tenants/consent-status?tenantId=<guid> (FD-111 v1.0) ------------
// Migration-agnostic admin read (EasyAuth per ADR-014). A lightweight,
// non-blocking probe of a tenant's admin-consent state, used by the create
// page to surface source-tenant consent DURING migration creation. It is a
// separate read — never inside the create path (new-migration stays call-free
// per FD-ADR-016).
//
// Contract row (docs/TASKS.md API Contract Changes, FD-111 v1.0):
//   200 data.{tenantId, consented, failureClass, missingRoles[], consentUrl}
//       — a verdict is 200 even when the answer is "needs consent" (this is a
//         status read, not a gated operation, unlike the FD-108 409).
//   400 INVALID_TENANT_ID  — missing / non-GUID tenantId
//   502 GRAPH_ERROR        — verdict undeterminable (network/5xx/config);
//                            NEVER collapsed to consented:false.
//
// The `data` field shape matches the FD-108 409 consent block, so the UI
// reuses one renderer (ConsentMessageBar). Keep this aligned with that row and
// with ConsentErrorData in src/types/identity.ts (frontend/CLAUDE.md sync rule).

/** Unwrapped `data` payload of GET /v1/tenants/consent-status (FD-111 v1.0). */
export interface TenantConsentStatus {
  /** The tenant GUID the verdict is for (echoed back, normalized server-side). */
  tenantId: string;
  /** True only when `failureClass === 'None'` — the tenant has consented. */
  consented: boolean;
  /** 'None' (consented) | 'NotConsented' | 'ConsentDrift'. */
  failureClass: string;
  /** Graph app-roles still needed; `[]` when consented. */
  missingRoles: string[];
  /**
   * Bare admin-consent URL (FD-109). Present on both consented and
   * consent-class-unconsented verdicts; `null` only in the error case (which
   * surfaces as a 502, not a 200 body).
   */
  consentUrl: string | null;
}

/** The machine `error.code`s the consent-status endpoint returns (FD-111 v1.0). */
export type TenantConsentStatusErrorCode = 'INVALID_TENANT_ID' | 'GRAPH_ERROR';

export async function getConsentStatus(tenantId: string): Promise<TenantConsentStatus> {
  const envelope = await getJson<ApiEnvelope<TenantConsentStatus>>(
    `/v1/tenants/consent-status?tenantId=${encodeURIComponent(tenantId)}`,
  );
  return envelope.data;
}

// Surfaces the consent-status endpoint's distinct machine codes from a thrown
// ApiError so callers can tell an undeterminable verdict (502 GRAPH_ERROR —
// "couldn't verify, retry", never blocking) apart from a bad request (400).
// Prefers the envelope `error.code`, falling back to the HTTP status mapping.
export function consentStatusErrorCode(
  err: unknown,
): TenantConsentStatusErrorCode | 'UNKNOWN' {
  if (!(err instanceof ApiError)) return 'UNKNOWN';

  const envelopeCode = readEnvelopeErrorCode(err.body);
  if (envelopeCode === 'INVALID_TENANT_ID' || envelopeCode === 'GRAPH_ERROR') {
    return envelopeCode;
  }

  switch (err.status) {
    case 400:
      return 'INVALID_TENANT_ID';
    case 502:
      return 'GRAPH_ERROR';
    default:
      return 'UNKNOWN';
  }
}
